/**
 * Addon Transfer API Contract Schemas
 *
 * Request and response shapes for the addon-transfer endpoints
 * (ticketing-service AddonTransferController). All timestamps are ISO strings
 * (wire format); the controller serializes Prisma Date fields at the boundary.
 *
 * Transfers are atomic: sending requires the sender to scan the recipient's
 * wristband QR, which is the bilateral acknowledgement, so `POST /addon-transfers`
 * moves the tickets in one transaction and returns a terminal result. There is
 * no pending state and therefore no status vocabulary crossing HTTP.
 */

import { z } from 'zod';

// ============================================================================
// Request Schemas
// ============================================================================

export const CreateAddonTransferSchema = z.object({
  idempotencyKey: z.string().max(64),
  senderWristbandCode: z.string().min(1),
  recipientWristbandCode: z.string().min(1),
  ticketIds: z.array(z.string().uuid()),
  message: z.string().optional(),
});
export type CreateAddonTransferInput = z.infer<typeof CreateAddonTransferSchema>;

// ============================================================================
// Response Schemas
// ============================================================================

export const TransferableAddonSchema = z.object({
  ticketId: z.string(),
  ticketNumber: z.string(),
  attractionId: z.string(),
  attractionCode: z.string(),
  attractionName: z.string(),
  ticketTypeCode: z.string(),
  slotId: z.string().nullable(),
  slotDate: z.string().nullable(),
  slotStartTime: z.string().nullable(),
  status: z.string(),
  visitDate: z.string(),
  createdAt: z.string(),
  maxTransferQuantity: z.number().int(),
});
export type TransferableAddon = z.infer<typeof TransferableAddonSchema>;

export const TransferableAddonsResponseSchema = z.object({
  items: z.array(TransferableAddonSchema),
});
export type TransferableAddonsResponse = z.infer<typeof TransferableAddonsResponseSchema>;

export const CreateAddonTransferResponseSchema = z.object({
  transferId: z.string().uuid(),
  transferNumber: z.string(),
  recipientWristbandCode: z.string(),
  transferredTicketIds: z.array(z.string().uuid()),
});
export type CreateAddonTransferResponse = z.infer<typeof CreateAddonTransferResponseSchema>;
